import type { SiteUpdatePayload, Site, SiteInsertPayload, CustomDomainRequest } from '@/types/site.types';
import type { UserSubscription } from '@/services/subscription-service';
import type { CustomDomainValidationRecords } from '@stackje/common/config';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || window.location.origin;

export class ApiClient {
  private static async makeAuthenticatedRequest(
    url: string,
    options: RequestInit = {}
  ): Promise<Response> {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      credentials: 'include',
    });
    
    return response;
  }

  static async insertSite(siteData: SiteInsertPayload): Promise<{ data: Site | null; error: { message: string } | null }> {
    try {
      const response = await this.makeAuthenticatedRequest(`${API_BASE_URL}/api/sites`, {
        method: 'POST',
        body: JSON.stringify(siteData),
      });

      if (!response.ok) {
        try {
          const errorData = await response.json() as { error: string };
          return { data: null, error: { message: errorData.error || 'Failed to create site' } };
        } catch {
          // If JSON parsing fails, return a generic error with status info
          return { data: null, error: { message: `Failed to create site (Status: ${response.status})` } };
        }
      }

      const data = await response.json();
      return { data: data as Site, error: null };
    } catch (error) {
      console.error('Site creation network error:', error);
      return { data: null, error: { message: 'Please check your internet connection and try again.' } };
    }
  }

  static async getSiteByUserId(): Promise<{ data: Site | null; error: { message: string } | null }> {
    try {
      const response = await this.makeAuthenticatedRequest(`${API_BASE_URL}/api/user/site`, {
        method: 'GET',
      });

      if (!response.ok) {
        if (response.status === 404) {
          return { data: null, error: { message: 'No site found.' } };
        }
        const errorData = await response.json() as { error: string };
        return { data: null, error: { message: errorData.error || 'Failed to fetch site' } };
      }

      const data = await response.json();
      return { data: data as Site, error: null };
    } catch {
      return { data: null, error: { message: 'Network error occurred' } };
    }
  }

  static async getSiteById(id: string): Promise<{ data: Site | null; error: { message: string } | null }> {
    if (!id) {
      return { data: null, error: { message: 'Site ID cannot be empty.' } };
    }

    try {
      const response = await this.makeAuthenticatedRequest(`${API_BASE_URL}/api/sites/${id}`, {
        method: 'GET',
      });

      if (!response.ok) {
        if (response.status === 404) {
          return { data: null, error: { message: 'No site found.' } };
        }
        const errorData = await response.json() as { error: string };
        return { data: null, error: { message: errorData.error || 'Failed to fetch site' } };
      }

      const data = await response.json();
      return { data: data as Site, error: null };
    } catch {
      return { data: null, error: { message: 'Network error occurred' } };
    }
  }

  static async updateSite(
    id: string,
    siteData: SiteUpdatePayload
  ): Promise<{ data: Site | null; error: { message: string } | null }> {
    if (!id) {
      return { data: null, error: { message: 'Site ID cannot be empty.' } };
    }

    try {
      const response = await this.makeAuthenticatedRequest(`${API_BASE_URL}/api/sites/${id}`, {
        method: 'PUT',
        body: JSON.stringify(siteData),
      });

      if (!response.ok) {
        const errorData = await response.json() as { error: string };
        return { data: null, error: { message: errorData.error || 'Failed to update site' } };
      }

      const data = await response.json();
      return { data: data as Site, error: null };
    } catch {
      return { data: null, error: { message: 'Network error occurred' } };
    }
  }

  static async getUserSubscription(): Promise<{ data: UserSubscription | null; error: { message: string } | null }> {
    try {
      const response = await this.makeAuthenticatedRequest(`${API_BASE_URL}/api/user/subscription`, {
        method: 'GET',
      });

      if (!response.ok) {
        const errorData = await response.json() as { error: string };
        return { data: null, error: { message: errorData.error || 'Failed to fetch subscription' } };
      }

      const data = await response.json();
      return { data: data as UserSubscription, error: null };
    } catch {
      return { data: null, error: { message: 'Network error occurred' } };
    }
  }

  static async configureCustomDomain(
    siteId: string,
    domainData: CustomDomainRequest
  ): Promise<{
    data: { domain: string; validationRecords: CustomDomainValidationRecords } | null;
    error: { message: string } | null;
  }> {
    if (!siteId) {
      return { data: null, error: { message: 'Site ID cannot be empty.' } };
    }

    try {
      const response = await this.makeAuthenticatedRequest(`${API_BASE_URL}/api/sites/${siteId}/custom-domain`, {
        method: 'POST',
        body: JSON.stringify(domainData),
      });

      if (!response.ok) {
        const errorData = await response.json() as { error: string };
        return { data: null, error: { message: errorData.error || 'Failed to configure custom domain' } };
      }

      const data = await response.json();
      return {
        data: {
          domain: data.domain,
          validationRecords: data.validationRecords
        },
        error: null
      };
    } catch {
      return { data: null, error: { message: 'Network error occurred' } };
    }
  }

  static async getCustomDomainStatus(siteId: string): Promise<{
    data: {
      domain: string;
      status: string;
      sslStatus: string;
      verificationErrors?: string[];
      validationRecords: CustomDomainValidationRecords | null;
    } | null;
    error: { message: string } | null;
  }> {
    if (!siteId) {
      return { data: null, error: { message: 'Site ID cannot be empty.' } };
    }

    try {
      const response = await this.makeAuthenticatedRequest(`${API_BASE_URL}/api/sites/${siteId}/custom-domain/status`, {
        method: 'GET',
      });

      if (!response.ok) {
        const errorData = await response.json() as { error: string };
        return { data: null, error: { message: errorData.error || 'Failed to get custom domain status' } };
      }

      const data = await response.json();
      return { data, error: null };
    } catch {
      return { data: null, error: { message: 'Network error occurred' } };
    }
  }

  static async removeCustomDomain(siteId: string): Promise<{
    data: { success: boolean } | null;
    error: { message: string } | null;
  }> {
    if (!siteId) {
      return { data: null, error: { message: 'Site ID cannot be empty.' } };
    }

    try {
      const response = await this.makeAuthenticatedRequest(`${API_BASE_URL}/api/sites/${siteId}/custom-domain`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json() as { error: string };
        return { data: null, error: { message: errorData.error || 'Failed to remove custom domain' } };
      }

      const data = await response.json();
      return { data, error: null };
    } catch {
      return { data: null, error: { message: 'Network error occurred' } };
    }
  }
}